/**
 * Production system: buildings train queued units. Cost/pop are deducted at
 * enqueue time (see commands.ts); here we advance the timer, spawn the finished
 * unit ADJACENT to the building, then apply the building's rally order — move to
 * the rally point, or gather the rallied resource (continuing nearby when it
 * depletes).
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import type { Rally } from "../components";
import { FIXED_DT, type Vec2 } from "../types";
import { spawnUnit } from "../spawn";
import { applyIntent } from "../orders";
import { pointSegmentDistance, rampartGeometry, rampartPush, rampartSegmentBlocked, type RampartGeometry } from "../ramparts";
import { terrainSegmentClear } from "../pathfinding";

/** Where a freshly-trained unit is headed, so it emerges on THAT side of the
 *  building (at the gate it walks out of) instead of a fixed corner. A LAND unit
 *  aims at its rally tile (the rallied resource node when set to gather); a SHIP
 *  at a WATER rally, else the open water beside the pier. No usable target → the
 *  building centre (the old nearest-corner behaviour). Deterministic. */
function spawnTarget(world: World, col: number, row: number, center: Vec2, rally: Rally | undefined, naval: boolean): Vec2 {
  if (naval) {
    if (rally) {
      const rh = world.hexAtWorld(rally.x, rally.y);
      if (rh && world.isWater(rh.col, rh.row)) return { x: rally.x, y: rally.y };
    }
    const dw = world.dockWater(col, row);
    return dw ? world.hexCenter(dw[0], dw[1]) : center;
  }
  if (rally) {
    if (rally.resource != null && world.resource.has(rally.resource)) {
      const nt = world.transform.get(rally.resource);
      if (nt) return { x: nt.pos.x, y: nt.pos.y };
    }
    return { x: rally.x, y: rally.y };
  }
  return center;
}

/** Nearest WATER resource node (fish school) to a point, for auto-fishing — only
 *  nodes a ship can actually reach. Deterministic (lower-eid tie-break). */
function nearestWaterNode(world: World, x: number, y: number, maxDist: number): number | null {
  let best: number | null = null;
  let bestD = maxDist * maxDist;
  for (const [eid] of world.resource) {
    if (world.ident.get(eid)?.kind !== "resource") continue;
    const tr = world.transform.get(eid);
    if (!tr) continue;
    const h = world.hexAtWorld(tr.pos.x, tr.pos.y);
    if (!h || !world.isWater(h.col, h.row)) continue;
    const d = (tr.pos.x - x) ** 2 + (tr.pos.y - y) ** 2;
    if (d < bestD || (d === bestD && eid < (best ?? Infinity))) { bestD = d; best = eid; }
  }
  return best;
}

/** Find model-clear standing space inside one visual hex. The producer centre is
 * allowed as the start of the short emergence sweep, but the result may not
 * overlap any prop/rampart or cross a rampart on the way out. */
function spawnPoint(
  world: World,
  producer: number,
  col: number,
  row: number,
  toward: Vec2,
  radius: number,
  owner: number,
  walls: RampartGeometry,
): Vec2 | null {
  const center = world.hexCenter(col, row);
  const from = world.transform.get(producer)?.pos;
  if (!from) return null;
  const team = world.players.get(owner)?.team;
  const producerKey = world.hkey(world.building.get(producer)!.origin.x, world.building.get(producer)!.origin.y);
  const candidates: Vec2[] = [center];
  for (const distance of [0.24, 0.48, 0.72]) {
    for (let i = 0; i < 24; i++) {
      const angle = (i * Math.PI * 2) / 24;
      candidates.push({
        x: center.x + Math.cos(angle) * world.size * distance,
        y: center.y + Math.sin(angle) * world.size * distance,
      });
    }
  }

  let best: Vec2 | null = null;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    const hex = world.hexAtWorld(candidate.x, candidate.y);
    if (!hex || hex.col !== col || hex.row !== row || !world.passableFor("land", col, row, owner)) continue;
    if (!terrainSegmentClear(world, from.x, from.y, candidate.x, candidate.y, "land", owner)) continue;
    if (rampartPush(walls, candidate.x, candidate.y, radius, team) !== null) continue;
    if (rampartSegmentBlocked(walls, from.x, from.y, candidate.x, candidate.y, radius, team)) continue;

    let clear = true;
    for (const [key, prop] of world.propPos) {
      const clearance = prop.r + radius;
      if (Math.hypot(candidate.x - prop.x, candidate.y - prop.z) < clearance) { clear = false; break; }
      if (key !== producerKey && pointSegmentDistance(prop.x, prop.z, from.x, from.y, candidate.x, candidate.y) < clearance) {
        clear = false;
        break;
      }
    }
    if (!clear) continue;
    const distance = (candidate.x - toward.x) ** 2 + (candidate.y - toward.y) ** 2;
    if (distance < bestDistance) { bestDistance = distance; best = candidate; }
  }
  return best;
}

export function productionSystem(world: World, reg: Registry): void {
  const walls = rampartGeometry(world);
  for (const [bid, prod] of world.producer) {
    if (prod.queue.length === 0) continue;
    const item = prod.queue[0];
    item.time -= FIXED_DT;
    if (item.time > 0) continue;

    const bTr = world.transform.get(bid);
    const b = world.building.get(bid);
    const rally = world.rally.get(bid);
    const owner = world.owner.get(bid)?.player;
    const civ = world.ident.get(bid)?.civ;
    if (!bTr || !b || owner === undefined || !civ) {
      prod.queue.shift();
      continue;
    }

    // Ships emerge onto WATER; land units onto any PASSABLE neighbour — including a
    // tile that holds another building (houses/fields have sub-tile footprints units
    // walk through), so a town centre RINGED BY BUILDINGS still spawns (the unit pops
    // out and steers clear). Only a tile that's genuinely solid (a wall) or water
    // blocks a land spawn. isWalkable (tile completely free) was too strict and froze
    // production inside a packed base.
    const unit = reg.unit(civ, item.unitId);
    const naval = unit.class === "naval";
    const radius = (unit.radius ?? 0.18) * world.size;

    // Emerge on the side of the building FACING where the unit is headed (its
    // rally), so it appears at the gate it walks out of instead of popping at a
    // fixed corner and then crossing the footprint. For a ship, head toward a
    // WATER rally or the open water beside the pier; a land unit, toward the rally
    // tile (the resource node when rallied to gather). No rally → the centre, i.e.
    // the old nearest-corner pick.
    const toward = spawnTarget(world, b.origin.x, b.origin.y, bTr.pos, rally, naval);
    let spawnCol = -1, spawnRow = -1, spawnAt: Vec2 | null = null, bd = Infinity;
    for (const [nc, nr] of world.neighbors(b.origin.x, b.origin.y)) {
      const ok = naval ? world.passableFor("naval", nc, nr, owner) : world.passableFor("land", nc, nr, owner);
      if (!ok) continue;
      const c = world.hexCenter(nc, nr);
      const point = naval ? c : spawnPoint(world, bid, nc, nr, toward, radius, owner, walls);
      if (!point) continue;
      const d = (point.x - toward.x) ** 2 + (point.y - toward.y) ** 2;
      // lower-(col,row) tie-break keeps the side pick iteration-order-independent.
      if (d < bd || (d === bd && (nc < spawnCol || (nc === spawnCol && nr < spawnRow)))) {
        bd = d; spawnCol = nc; spawnRow = nr; spawnAt = point;
      }
    }

    // No free adjacent hex (building boxed in)? Keep the finished item queued and
    // retry next tick — the cost was already paid at enqueue, so shifting it here
    // would silently destroy both the unit and the resources spent on it.
    if (spawnCol < 0) continue;

    const c = spawnAt ?? world.hexCenter(spawnCol, spawnRow);
    const eid = spawnUnit(world, reg, civ, item.unitId, owner, c);
    if (naval) {
      // A dock's default rally is its LAND shore (where workers stand) — useless to
      // a ship. Respect only a rally the player set ON WATER; otherwise fishing
      // boats auto-fish the nearest school and warships/transports clear the pier
      // to open water.
      const rh = rally ? world.hexAtWorld(rally.x, rally.y) : null;
      const rallyOnWater = !!rh && world.isWater(rh.col, rh.row);
      if (rallyOnWater) {
        applyIntent(world, eid, { kind: "move", x: rally!.x, y: rally!.y });
      } else if (world.gatherer.has(eid)) {
        const fish = nearestWaterNode(world, c.x, c.y, world.size * 50);
        if (fish !== null) applyIntent(world, eid, { kind: "gather", node: fish });
        else moveToOpenWater(world, eid, b.origin.x, b.origin.y, c);
      } else {
        moveToOpenWater(world, eid, b.origin.x, b.origin.y, c);
      }
    } else if (rally) {
      if (rally.resource != null && world.resource.has(rally.resource) && world.gatherer.has(eid)) {
        applyIntent(world, eid, { kind: "gather", node: rally.resource });
      } else {
        applyIntent(world, eid, { kind: "move", x: rally.x, y: rally.y });
      }
    }
    prod.queue.shift();
  }
}

/** Send a freshly-built ship to open water beside the dock (clears the pier). */
function moveToOpenWater(world: World, eid: number, dockCol: number, dockRow: number, fallback: { x: number; y: number }): void {
  const dw = world.dockWater(dockCol, dockRow);
  const pt = dw ? world.hexCenter(dw[0], dw[1]) : fallback;
  applyIntent(world, eid, { kind: "move", x: pt.x, y: pt.y });
}
